// A QR symbol as inline SVG.
//
// INLINE, AND THE REASON IS THE SECRET. The alternatives to inline SVG are an
// <img src> pointing somewhere, or a canvas. An <img> with a remote src would
// send the provisioning URI — which is to say the shared secret — to whoever
// serves it, and a QR image service is the textbook version of that mistake;
// there is no configuration of it that is acceptable, so the shape of this file
// removes the possibility rather than documenting it. A data: URI would keep the
// secret local but puts it in an attribute that a screenshot tool, an extension
// or a copied element carries off wholesale. A canvas cannot be read by the
// check without a rasteriser and cannot be zoomed by the person scanning it.
//
// ONE PATH, NOT ~1400 RECTS. A version 9 symbol is 53 × 53 with roughly half its
// modules dark, and a <rect> each would be well over a thousand nodes for one
// decorative element. The runs are merged along each row into a single path,
// which is one node and is also why check.mjs can parse the `d` back into a
// matrix and compare it against another implementation.
//
// THE QUIET ZONE IS DRAWN, NOT ASSUMED. Four light modules on every side are part
// of the symbol, not padding around it: many readers will not attempt a scan
// without them. Drawing an explicit light rect rather than letting the card
// behind show through means the margin stays light even if this component is
// ever placed on something that is not the card.

import type { ReactNode } from "react";
import { QR_QUIET_ZONE, type QrMatrix } from "./encode.js";

/**
 * The dark modules of `matrix` as SVG path data, in module units, offset by the
 * quiet zone.
 *
 * Horizontal runs are merged and vertical ones are not. Merging one axis removes
 * the node count problem; merging both is a rectangle-covering problem whose
 * answer is a handful of bytes smaller and which no longer round-trips into a
 * matrix by inspection.
 */
export function qrPathData(matrix: QrMatrix): string {
  const parts: string[] = [];

  for (let row = 0; row < matrix.size; row += 1) {
    let column = 0;
    while (column < matrix.size) {
      if (matrix.modules[row * matrix.size + column] !== 1) {
        column += 1;
        continue;
      }

      let run = 1;
      while (column + run < matrix.size && matrix.modules[row * matrix.size + column + run] === 1) {
        run += 1;
      }

      const x = column + QR_QUIET_ZONE;
      const y = row + QR_QUIET_ZONE;
      parts.push(`M${x} ${y}h${run}v1h-${run}z`);
      column += run;
    }
  }

  return parts.join("");
}

export type QrProps = {
  readonly matrix: QrMatrix;
  /**
   * What a screen reader says instead of the symbol.
   *
   * Required rather than defaulted so that a caller cannot get an unlabelled
   * image by forgetting, and it is the caller's because only the caller knows
   * what the symbol is for.
   *
   * MUST NOT CONTAIN THE SECRET. A label is copied by assistive technology,
   * read aloud in rooms, and surfaced by tooling that never touches the visible
   * page; the secret has one home on this screen and it is the field beside the
   * symbol that a person chose to reveal. check.mjs asserts the rendered label
   * does not contain it.
   */
  readonly label: string;
};

export function Qr({ matrix, label }: QrProps): ReactNode {
  const extent = matrix.size + 2 * QR_QUIET_ZONE;

  return (
    <svg
      className="console-qr"
      // The viewBox is in module units and there is no width or height, so the
      // symbol scales with the CSS box and a module never lands on a fraction of
      // a device pixel by a rounding this file performed.
      viewBox={`0 0 ${extent} ${extent}`}
      // crispEdges rather than the default, because antialiasing a module edge
      // grey is exactly the contrast a reader is measuring across.
      shapeRendering="crispEdges"
      role="img"
      aria-label={label}
    >
      <rect className="console-qr-light" width={extent} height={extent} />
      <path className="console-qr-dark" d={qrPathData(matrix)} />
    </svg>
  );
}
